import Element from '../components/Element';
import ajax from '../utils/ajax';

/**
 * 数据源基类
 * 实现的url的组装和发起ajax请求
 */
export default class Source extends Element {
    /**
     * 数据源对象的初始化方法.
     * @param {string} name    - 数据源名称，全局唯一
     * @param {object} config  - 配置项
     * @param {object} options - 其他可选项
     * @returns {undefined}
     */
    constructor(name, config, options) {
        super(options);

        /**
         * 数据源名称
         * @type {string}
         */
        this.name = name;
        /**
         * 数据源请求地址
         * @type {null|string}
         */
        this._url = config.url || null;
        /**
         * 数据源的子域名配置
         * @type {array<string>}
         */
        this._subdomains = config.subdomains || [];
        /**
         * 占位符
         * @type {object}
         */
        this._placeholders = config.placeholders || {};
        /**
         * 请求头信息
         * @type {object}
         */
        this._headers = config.headers;
        /**
         * 请求的返回类型：json|arraybuffer
         * @type {array<string>}
         */
        this._responseType = config.responseType || 'arraybuffer';
        /**
         * 子域名的负载信息
         * 初始化都为0
         * property {Object}
         */
        this._subdomainsPayload = {};
        this._subdomains.forEach(it => {
            this._subdomainsPayload[it] = 0;
        });
    }

    /**
     * 添加到地图中
     * @param {Object}  map 地图对象
     */
    addTo(map) {
        if (map && map.SourceController) {
            return super.addTo(map.SourceController);
        }
        return null;
    }

    onAdd(controller) {
        super.onAdd(controller);

        this._map = controller.map;
    }

    onRemove() {
        this._map = null;

        super.onRemove();
    }

    /**
     * 设置占位符值
     * @param {string} key - 占位符key
     * @param {any}    value - 占位符值
     * @returns {undefined}
     */
    setPlaceholder(key, value) {
        this._placeholders[key] = value;
    }

    /**
     * 获取占位符值
     * @param {string} key - 占位符key
     * @returns {any} - 占位符值
     */
    getPlaceholder(key) {
        return this._placeholders[key];
    }

    /**
     * 创建请求
     * @param   {array} params - 请求参数
     * @param   {object} 请求的控制参数
     * @returns {object} 包含promise和abort函数的请求对象
     */
    _createRequest(params) {
        const subdomain = this._getSubdomain();
        const defaultPlaceholders = {
            s: subdomain
        };

        const placeholders = Object.assign(defaultPlaceholders, this._placeholders, params);

        const request = ajax.$get(encodeURI(this._template(this._url, placeholders)), null, {
            responseType: this._responseType,
            headers: this._headers
        });

        // 必须重新赋值，获取promise链上最新的promise对象
        request.promise = request.promise.finally(() => {
            if (subdomain) {
                this._subdomainsPayload[subdomain]--;
            }
        });

        return request;
    }

    /**
     * 分配子域名
     * 优先分配负载最少的
     * @returns {string} - 子域名
     */
    _getSubdomain() {
        let subdomain;
        let min = 9999;
        for (const [k, v] of Object.entries(this._subdomainsPayload)) {
            if (v < min) {
                min = v;
                subdomain = k;
            }
        }

        if (subdomain) {
            this._subdomainsPayload[subdomain]++;

            return subdomain;
        }
        return null;
    }

    /**
     * url地址转换方法，将url模板里的占位符替换成具体的请求参数
     * @param {string}   str - 请求url模板
     * @param {object}   data - 请求参数
     * @returns {string} url字符串
     */
    _template(str, data) {
        return str.replace(/\{ *([\w_]+) *\}/g, (matchStr, key) => {
            if (!data.hasOwnProperty(key)) {
                throw new Error(`没有提供可用的值:${matchStr}`);
            }

            if (typeof data[key] === 'object') {
                return JSON.stringify(data[key]);
            }
            return data[key];
        });
    }
}
